import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authClient, getJWTToken } from '../lib/auth-client';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined);   // undefined = still loading
  const [loading, setLoading] = useState(true);

  const refreshSession = useCallback(async () => {
    setLoading(true);

    // Primary: ask Neon Auth directly (cross-origin, uses session cookie on Neon Auth domain)
    // Also handles OAuth callback via neon_auth_session_verifier param
    const { data: sessionData } = await authClient.getSession().catch(() => ({ data: null }));

    if (!sessionData?.user) {
      setUser(null);
      setLoading(false);
      return;
    }

    // Secondary: call our backend with JWT to provision the DB profile and get isAdmin
    try {
      const token = await getJWTToken().catch(() => null);
      const res = await fetch('/api/auth/me', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.authenticated) {
          setUser(data.user);
          setLoading(false);
          return;
        }
      }
    } catch {}

    // Fallback: user is authenticated via Neon Auth but backend profile failed
    // Still mark as logged in (isAdmin defaults to false)
    setUser(sessionData.user);
    setLoading(false);
  }, []);

  useEffect(() => {
    refreshSession();
  }, [refreshSession]);

  const login = async (email, password) => {
    const { error } = await authClient.signIn.email({ email, password });
    if (error) return { success: false, error: error.message || 'Credenziali non valide' };
    await refreshSession();
    return { success: true };
  };

  const register = async (name, email, password) => {
    const { error } = await authClient.signUp.email({ name, email, password });
    if (error) return { success: false, error: error.message || 'Errore nella registrazione' };
    await refreshSession();
    return { success: true };
  };

  const loginWithGoogle = () =>
    authClient.signIn.social({ provider: 'google', callbackURL: '/dashboard' });

  const logout = async () => {
    await authClient.signOut();
    setUser(null);
    setLoading(false);
  };

  const updateProfile = async (data) => {
    const { error } = await authClient.updateUser({ name: data.username || data.name });
    if (error) throw new Error(error.message);
    await refreshSession();
  };

  const changePassword = async (currentPassword, newPassword) => {
    const { error } = await authClient.changePassword({ currentPassword, newPassword, revokeOtherSessions: false });
    if (error) throw new Error(error.message);
  };

  const deleteAccount = async () => {
    const { error } = await authClient.deleteUser();
    if (error) throw new Error(error.message);
    setUser(null);
  };

  const userObj = user
    ? {
        id:       user.id,
        email:    user.email,
        name:     user.name,
        username: user.name,
        image:    user.image,
        isAdmin:  user.isAdmin,
      }
    : null;

  return (
    <AuthContext.Provider value={{
      user:            userObj,
      loading,
      isAuthenticated: !!user,
      login,
      register,
      loginWithGoogle,
      logout,
      updateProfile,
      changePassword,
      deleteAccount,
      refreshSession,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
